/* ============================================================
   PebbleBloom - Accordion Motion
   File: assets/pb-accordion.js

   Animates <details> open and close, and enforces one-open-at-a-time
   within each group.

   CSS alone cannot animate this. A closed <details> hides its
   children with display:none, so there is no height to transition
   from on open, and on close the browser removes the content the
   instant the attribute goes. The height is driven directly and
   [open] is removed only after the closing slide finishes.

   The click is captured, not merely bound. This theme ships its own
   accordion handler, and two scripts toggling the same element was
   what made the animation stutter - capture plus stopPropagation
   means exactly one handler runs.

   Three things here exist specifically to keep it feeling instant:

   1. Interruptible. An in-flight animation is cancelled and replaced,
      starting from whatever height the panel is at right now. The
      previous version refused any click until the current slide had
      finished, so a second tap inside ~200ms did nothing at all -
      which reads as lag even though the code was "working".

   2. Driven by the Web Animations API, not inline transitions plus a
      setTimeout. The timeout was only ever an estimate of when the
      transition ended; the finished promise is the real thing. It
      also removes the forced synchronous layout that the old
      `void offsetHeight` flush caused on every single click.

   3. Reads batched ahead of writes, and layout contained to the panel
      while it moves. With 15 accordions on a 12,000px page, an
      uncontained height animation relayouts the whole document on
      every frame.

   PB_DURATION must stay in step with --pb-acc-duration in
   pb-overrides.css, which times the caret and the content fade.

   Purely additive: without this file the accordions still open and
   close, just instantly. Nothing is hidden by script.
   ============================================================ */

(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var BASE = 220;
  var EASE = 'cubic-bezier(0.33, 0, 0.2, 1)';
  var supportsWAAPI =
    typeof Element !== 'undefined' &&
    typeof Element.prototype.animate === 'function';

  // Panel -> its running animation, so a click can interrupt one.
  var running = new WeakMap();

  function contentOf(d) {
    return (
      d.querySelector('.accordion__content') ||
      d.querySelector('.collapsible-content__content') ||
      d.querySelector('summary + *')
    );
  }

  /* Group = nearest shared ancestor holding several accordions. Used
     so opening a buy-box FAQ never closes one in the FAQ section. */
  function groupOf(d) {
    return (
      d.closest('.collapsible-content__wrapper') ||
      d.closest('.product__info-container') ||
      d.closest('.shopify-section') ||
      document
    );
  }

  function siblings(d) {
    var g = groupOf(d);
    return Array.prototype.filter.call(
      g.querySelectorAll('details[data-pb-acc-init="1"]'),
      function (x) {
        return x !== d && x.open && groupOf(x) === g;
      }
    );
  }

  /* Short answers should not take as long as long ones, and a panel
     reversed when it is nearly closed should not replay a full slide.
     Both are scaled here rather than run at one flat duration. */
  function durationFor(distance, full) {
    var base = 170 + Math.min(80, full * 0.07);
    var ratio = full > 0 ? Math.min(1, distance / full) : 1;
    return Math.max(120, Math.round(base * (0.45 + 0.55 * ratio)));
  }

  function settle(d, c, opening) {
    // Attribute comes off last, or the content vanishes mid-slide.
    if (!opening) d.open = false;
    c.style.overflow = '';
    c.style.contain = '';
    c.style.willChange = '';
    c.style.height = '';
  }

  function slide(d, opening) {
    var c = contentOf(d);
    if (!c || !supportsWAAPI || reduced) {
      d.open = opening;
      d.classList.toggle('pb-acc-open', opening);
      return;
    }

    var previous = running.get(c);
    var startHeight = null;

    if (previous) {
      // Take the height it is at right now, then drop the old animation
      // so its fill stops holding the panel.
      startHeight = c.getBoundingClientRect().height;
      previous.cancel();
      running.delete(c);
    }

    if (opening) d.open = true;
    d.classList.toggle('pb-acc-open', opening);

    // -- reads, all of them, before any write --
    var full = c.scrollHeight;
    if (startHeight === null) startHeight = opening ? 0 : full;
    var endHeight = opening ? full : 0;
    var dur = durationFor(Math.abs(endHeight - startHeight), full || 1);

    // -- writes --
    c.style.overflow = 'hidden';
    c.style.willChange = 'height';
    // Keeps the per-frame relayout inside this panel instead of
    // reflowing the whole page behind it.
    c.style.contain = 'layout paint';

    var anim = c.animate(
      [{ height: startHeight + 'px' }, { height: endHeight + 'px' }],
      { duration: dur, easing: EASE, fill: 'both' }
    );
    running.set(c, anim);

    var finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      window.clearTimeout(watchdog);
      if (running.get(c) !== anim) return; // a newer click owns it now
      running.delete(c);
      settle(d, c, opening);
      anim.cancel(); // release fill so the panel returns to auto height
    }

    /* The finished promise only settles while the document is actually
       animating. In a backgrounded tab, or any frame-starved state, it
       can simply never resolve - and because the height fill and the
       [open] attribute are both cleared in that handler, the panel
       would stay wedged at the wrong height with the attribute out of
       step with the class. This forces the same cleanup slightly after
       the slide should have ended. */
    var watchdog = window.setTimeout(finish, dur + 120);

    anim.finished.then(finish, function () {
      // Cancelled by a newer click; that animation owns the cleanup.
      finished = true;
      window.clearTimeout(watchdog);
    });
  }

  function wire(d) {
    if (d.dataset.pbAccInit === '1') return;
    var summary = d.querySelector('summary');
    if (!summary || !contentOf(d)) return;
    d.dataset.pbAccInit = '1';
    if (d.open) d.classList.add('pb-acc-open');

    summary.addEventListener(
      'click',
      function (e) {
        if (reduced) return; // native behaviour is fine
        e.preventDefault();
        e.stopPropagation();

        var wasOpen = d.open;

        if (!wasOpen) {
          siblings(d).forEach(function (o) {
            slide(o, false);
          });
        }

        slide(d, !wasOpen);
      },
      true // capture, so the theme's own handler never also fires
    );
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(root.querySelectorAll('details'), function (d) {
      if (d.closest('.accordion, .collapsible-content, .product__accordion')) wire(d);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
